/** The issuer public keys a wallet trusts, kid -> raw 32 bytes.
 *
 * Fetched from `GET /api/clave` at enrolment and stored in IndexedDB, so
 * verification still never reaches the network: it reads what was stored. It
 * used to be baked in at build time, and that could not work on a UH server,
 * where the issuer mints its private key on first start and the application is
 * built earlier, on another machine. Trusting a key the same origin serves
 * weakens nothing, because that origin also serves the code that verifies:
 * whoever can change one can change the other. */

/** The raw 32 bytes behind one base64url issuer key, or an error saying which
 *  way the value is wrong.
 *
 * A key that decodes to the wrong length is refused here rather than stored,
 * because it would otherwise fail inside WebCrypto at the first scan, on a
 * phone with no network to fetch a better one. */
export function decodeIssuerKey(base64url: string): Uint8Array {
  let raw: Uint8Array;
  try {
    raw = Uint8Array.from(
      atob(base64url.replace(/-/g, "+").replace(/_/g, "/")),
      (c) => c.charCodeAt(0),
    );
  } catch {
    throw new Error(`issuer key is not base64url: ${JSON.stringify(base64url)}`);
  }
  if (raw.length !== 32) {
    throw new Error(`issuer key must decode to 32 bytes, got ${raw.length}`);
  }
  return raw;
}

/** The trust store the issuer reports, validated before anyone stores it.
 *
 * Rejects on anything short of a well-formed answer: a caller that stored a
 * half-read store would hold a wallet that refuses every credential it scans,
 * and blames the university for it. */
export async function fetchTrustStore(signal?: AbortSignal): Promise<Map<number, Uint8Array>> {
  const response = await fetch("/api/clave", { signal });
  if (!response.ok) throw new Error(`/api/clave answered ${response.status}`);
  const body = (await response.json()) as { kid?: unknown; public_key?: unknown };
  if (typeof body.kid !== "number" || !Number.isInteger(body.kid)) {
    throw new Error(`/api/clave reported no integer kid: ${JSON.stringify(body.kid)}`);
  }
  return new Map([[body.kid, decodeIssuerKey(String(body.public_key ?? ""))]]);
}
